/**
 * 推导 `isAwaitingFirstMessageAfterCreate`：
 * 欢迎页发送首条消息切到对话态时，会出现一个"已经 mount ConversationChatPanel
 * 但 user 消息还没塞进来"的窗口（创建 session 接口飞行 → 目录创建 → 文件上传 → 真正
 * setMessages 把 user/assistant 占位塞进来）。这个窗口里 messages 仍是空、且新 session
 * 不需要 history bootstrap，原本会闪 ChatEmptySession 一帧。
 *
 * 这里把判定收敛到一个纯函数，便于测试 + 避免在 hook 里写 ref state machine。
 *
 * 触发条件（命中任一即为 true）：
 * 1. `creatingSession` 为 true（POST /sessions 飞行中）
 * 2. `!sessionsId && activeSessionId`：history.replaceState 写完 URL 但 Next route param
 *    还没同步上来 → 此时 messages 还是空；只要这个窗口存在就走 ChatLoading。
 *
 * 一旦 `messagesLength > 0`（setMessages 把 user 占位塞进来），立刻 false，让
 * MessageList 接管渲染，避免标志位长挂。
 *
 * 另一条提前 false 的出口：`firstSendAbortedSessionId === activeSessionId`。
 * 欢迎页带暂存附件发送时，session 先被乐观建出并 setActiveSessionId，之后才上传附件；
 * 若附件内容审核未通过 / 上传失败，本轮没有 user 占位塞进来（messages 仍为 0），
 * 上层会通过该字段标记"首条消息已结束（失败）"，让页面落到新建 session 的空态而非永久 loading，
 * 输入框草稿保持不变，用户可改后重发。
 *
 * 不会错挂的场景：
 * - 欢迎页本身 (`!sessionsId && !activeSessionId && !creatingSession`) → false，
 *   保留 hero 渲染入口给 WelcomeChatPanel。
 * - 从 sidebar 进入已有 session (`sessionsId === activeSessionId`) → false，
 *   把 loading 留给 `isHistoryBootstrapping / historyLoading` 路径自己控制。
 * - 用户 reset 回 /claw 欢迎页（`activeSessionId` 被置空、messages 也清空）→ false。
 */
export interface AwaitingFirstMessageFlagInput {
  /** Next route 的 `[sessionsId]` 参数；从 sidebar 进入或直接打开 /claw/<id> 时存在 */
  sessionsId: string | undefined;
  /** store.activeSessionId：欢迎页发送时通过 `setActiveSessionId` 立即落地 */
  activeSessionId: string | null;
  /** create session 接口飞行中 */
  creatingSession: boolean;
  /** messages 数组长度（user 占位塞进来后立刻 > 0） */
  messagesLength: number;
  /**
   * 首条消息发送已被中止（内容审核未通过 / 暂存附件上传失败等）的 session id。
   * 命中当前 activeSessionId 时立刻结束等待态：保留新建 session 但展示空态，
   * 不再卡 loading，输入框草稿原样保留供用户改后重发。
   */
  firstSendAbortedSessionId?: string | null;
}

export const deriveIsAwaitingFirstMessageAfterCreate = ({
  sessionsId,
  activeSessionId,
  creatingSession,
  messagesLength,
  firstSendAbortedSessionId,
}: AwaitingFirstMessageFlagInput): boolean => {
  // user 占位已就位 → 闪现窗口结束，立刻让位给 MessageList 渲染。
  if (messagesLength > 0) return false;

  // 首条消息发送已被中止（且就是当前这个新建 session）：结束等待态，落到空态而非 loading。
  if (firstSendAbortedSessionId && firstSendAbortedSessionId === activeSessionId) return false;

  // create session 接口飞行中：从 useFetch 触发到响应回来这段窗口都算。
  if (creatingSession) return true;

  // history.replaceState 已经把 activeSessionId 落地，Next route param 还没同步上来。
  // 这是 setMessages 写入前最后的"无 history、无消息、无 loading"窗口。
  if (!sessionsId && activeSessionId) return true;

  return false;
};
